import axios from 'axios';
import { useEffect, useState } from 'react';
import ApiDatabase from 'server';
import UseContext from '../../../../../hooks/useContext';
import Button from '../../../../button';
import StarRating from '../../../../starRating/starRating';
import Tooltip from '../../../../tooltips/Tooltips';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import Modalheader from '../../../components/header';
import useModal from '../../../useModal';
import ReviewCard from '../../../../card/ReviewCard';
import { buildAddUserVivierPatches } from '../../../../../utils/vivierUserPatch';

const ReviewDetailSkeleton = () => (
  <div className="flex flex-col gap-6 animate-pulse">
    <div><div className="h-3 w-full bg-gray-200 rounded" /><div className="h-3 w-full bg-gray-200 rounded mt-1" /></div>
    <div className="flex justify-between gap-3">
      <div><div className="h-3 w-20 bg-gray-200 rounded" /><div className="h-3 w-40 bg-gray-200 rounded mt-1" /></div>
      <div className="flex gap-2"><div className="h-3 w-20 bg-gray-200 rounded" /><div className="h-3 w-20 bg-gray-200 rounded" /></div>
    </div>
    <div className="border rounded-lg p-4">
      <div className="flex flex-col gap-6">
        <div className="flex justify-between gap-3">
          <div><div className="h-3 w-40 bg-gray-200 rounded" /><div className="h-3 w-80 bg-gray-200 rounded mt-2" /></div>
          <div className="w-fit rounded-full p-2 bg-gray-100"><div className="h-3 w-3 bg-gray-200 rounded" /></div>
        </div>
        <div className="flex justify-between gap-3">
          <div className="h-3 w-60 bg-gray-200 rounded" />
          <div className="h-3 w-40 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  </div>
);

const ReviewCardSkeleton = () => (
  <div className="flex flex-col gap-4 animate-pulse">
    {[1, 2, 3].map((i) => (
      <div key={i} className="border rounded-lg p-4">
        <div className="flex flex-col gap-6">
          <div className="flex justify-between gap-3">
            <div><div className="h-3 w-40 bg-gray-200 rounded" /><div className="h-3 w-80 bg-gray-200 rounded mt-2" /></div>
            <div className="w-fit rounded-full p-2 bg-gray-100"><div className="h-3 w-3 bg-gray-200 rounded" /></div>
          </div>
          <div className="flex justify-between gap-3">
            <div className="h-3 w-60 bg-gray-200 rounded" />
            <div className="h-3 w-40 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const RatingTempWorkerValidation = () => {
  const { objID, setObjID, setReviewUntreatedDelete, refreshUntreated, setRefreshUntreated, setUsersVivier } = UseContext();
  const { toggle } = useModal();
  const token = localStorage.getItem('xsrfToken');
  const [review, setReview] = useState({});
  const [rating, setRating] = useState(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [step, setStep] = useState(1);
  const [viviers, setViviers] = useState([]);
  const [viviersSelected, setViviersSelected] = useState([]);
  const [reviewsMission, setReviewsMission] = useState([]);
  const [isWaitingReviewMission, setIsWaitingReviewMission] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsWaiting(true);
    ApiDatabase.getRatingTempWorker(
      { token, idTempWorkerRating: objID },
      (data) => { setReview(data); setIsWaiting(false); },
      (err) => { if (err.name !== 'CanceledError') console.error(err); }
    );
  }, [objID]);

  useEffect(() => {
    if (review?.user?._id && review?.mission?._id) {
      ApiDatabase.getRatingTempWorkerByMission(
        { token, idUser: review.user._id, idMission: review.mission._id },
        (data) => setRating(data.rating),
        () => setRating(null)
      );
    }
  }, [review]);

  const confirmAndClose = () => {
    setIsLoading(true);
    ApiDatabase.postTreatmentRatingTempWorker(
      { token, idTempWorkerReview: objID },
      () => {
        setIsLoading(false);
        setReviewUntreatedDelete(objID);
        setObjID(null);
        setRefreshUntreated(!refreshUntreated);
        toggle();
      },
      (err) => { if (err.name !== 'CanceledError') { console.error(err); setIsLoading(false); } }
    );
  };

  useEffect(() => {
    if (step === 2 && review?.user?._id) {
      axios.get(`${process.env.REACT_APP_API_URL}vivier/vivierAll?listUsers=${review.user._id}`)
        .then((res) => setViviers(res.data))
        .catch(console.error);
    }
    if (step === 3) {
      setIsWaitingReviewMission(true);
      ApiDatabase.getReviewsByTempworkerByMission(
        { token, idUser: review?.user?._id, idMission: review?.mission?._id },
        (data) => { setReviewsMission(data.reviews ?? []); setIsWaitingReviewMission(false); },
        (err) => { if (err.name !== 'CanceledError') { setReviewsMission([]); setIsWaitingReviewMission(false); } }
      );
    }
  }, [step]);

  const handleSubmitVivier = (e) => {
    e.preventDefault();
    if (!viviersSelected.length || !review?.user?._id) return;
    setIsLoading(true);
    axios.post(`${process.env.REACT_APP_API_URL}vivier/addVivier`, {
      token, listUsers: [review.user._id], listViviers: viviersSelected, vivierStatus: 'test',
    })
      .then((res) => {
        const patches =
          res.data?.some((user) => user.viviersAdded?.length) || res.data?.length
            ? res.data
            : buildAddUserVivierPatches({
                userIds: [review.user._id],
                vivierIds: viviersSelected,
                vivierCatalog: viviers,
                vivierStatus: 'test',
              });
        setUsersVivier(patches);
        confirmAndClose();
      })
      .catch((err) => { console.error(err); setIsLoading(false); });
  };

  const isUserInVivier = (vivier) => Boolean(vivier?.allUsersIncluded);

  const toggleVivier = (vivierId) => {
    setViviersSelected((prev) =>
      prev.includes(vivierId) ? prev.filter((id) => id !== vivierId) : [...prev, vivierId]
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>Observation de l'avis</Modalheader>
      <ModalBody>
        {step === 1 && (
          isWaiting ? <ReviewDetailSkeleton /> : (
            <div className="flex flex-col gap-6">
              <div className="text-gray-500">
                Consultez l'avis de {review?.user?.firstname} {review?.user?.lastname} pour
                la mission de {review?.mission?.title} n°{review?.mission?.id_mission} de {review?.name_commercial}.
              </div>
              <div className="flex justify-between gap-3">
                <div className="my-auto">
                  <div>{review?.user?.firstname} {review?.user?.lastname}</div>
                  <div className="text-gray-500">
                    Note en tant que <span className="underline">{review?.mission?.title}</span>
                  </div>
                </div>
                {rating !== null && (
                  <div className="my-auto flex gap-2">
                    <Tooltip text={`${Math.round(rating * 100) / 100}/5 avis positif`} placement="top">
                      <div><StarRating rating={rating} /></div>
                    </Tooltip>
                    <Button size="SM" color="SECONDARY" onClick={() => setStep(3)}>Voir les avis</Button>
                  </div>
                )}
              </div>
              <ReviewCard review={review} />
            </div>
          )
        )}

        {step === 2 && (
          viviers?.length > 0 ? (
            <div className="flex flex-col gap-2">
              {viviers.map((vivier) => {
                const alreadyIn = isUserInVivier(vivier);
                return (
                  <label key={vivier._id} className="flex items-center space-x-2">
                    <input
                      type="checkbox" className="checkbox-primary"
                      checked={alreadyIn || viviersSelected.includes(vivier._id)}
                      onChange={() => toggleVivier(vivier._id)}
                      disabled={alreadyIn}
                    />
                    <div className="flex flex-col pl-4">
                      <span>{vivier.name}</span>
                      <span className="ft-sm text-gray-400">{vivier.userCount ?? 0} candidats</span>
                    </div>
                  </label>
                );
              })}
            </div>
          ) : 'Aucun vivier disponible.'
        )}

        {step === 3 && (
          isWaitingReviewMission ? <ReviewCardSkeleton /> : (
            reviewsMission?.length > 0 && (
              <div className="flex flex-col gap-4">
                {reviewsMission.map((r) => <ReviewCard key={r._id} review={r} />)}
              </div>
            )
          )
        )}
      </ModalBody>
      <ModalFooter>
        {step === 1 && (
          <>
            <Button size="LG" color={isLoading ? 'DISABLED' : 'SECONDARY'} onClick={isLoading ? undefined : confirmAndClose}>
              {isLoading ? 'Confirmation ...' : 'Confirmer'}
            </Button>
            <Button size="LG" color={isLoading ? 'DISABLED' : 'PRIMARY'} onClick={isLoading ? undefined : () => setStep(2)}>
              Ajouter dans un vivier
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Button size="LG" color={isLoading ? 'DISABLED' : 'SECONDARY'} onClick={isLoading ? undefined : () => setStep(1)}>
              Retour
            </Button>
            <Button size="LG" color={isLoading ? 'DISABLED' : 'PRIMARY'} onClick={isLoading ? undefined : handleSubmitVivier}>
              {isLoading ? 'Confirmation ...' : 'Confirmer'}
            </Button>
          </>
        )}
        {step === 3 && (
          <Button size="LG" color="SECONDARY" onClick={() => setStep(1)}>Retour</Button>
        )}
      </ModalFooter>
    </>
  );
};

export default RatingTempWorkerValidation;
